import { AnyAction, Dispatch } from 'redux';

import { type CustomRouterHistory } from '../createHistory';
import { type GRRound } from '../modules/gift-race/types/round';
import { type StateType } from '../states';
import { type OutcomeState } from '../states/entities/outcomes';
import { type BetTemplate } from '../types/bet';
import { type CardMode } from '../types/common';
import { type OutcomeStatus } from '../types/history';
import { type Route } from '../types/routes';
import { type SeparatorType } from '../types/separator';
import { SportId } from '../types/sports';
import { type TabBarNavigationType } from '../types/tabBar';

export type GetBetslipIdFromPropsType = {
  betslipId?: number;
  id?: number;
};
export const getBetslipIdFromProps = (state: StateType, { betslipId, id }: GetBetslipIdFromPropsType = {}) =>
  betslipId ?? id;

export type GetItemIdOrOddIdFromPropsType = {
  itemId?: string | number;
  oddId?: string | number;
  oddsId?: string | number;
};
export const getItemIdOrOddIdFromProps = (
  state: StateType,
  { itemId, oddId, oddsId }: GetItemIdOrOddIdFromPropsType = {},
) => itemId ?? oddId ?? oddsId;

export type GetItemIdFromPropsType = {
  itemId?: string | number;
};
export const getItemIdFromProps = (_: StateType, { itemId }: GetItemIdFromPropsType = {}) => itemId;

export type GetOutcomeIdOrOddsIdFromPropsType = {
  oddsId?: number;
  oddId?: number;
  outcomeId?: number;
};
export const getOutcomeIdOrOddsIdFromProps = (
  state: StateType,
  { oddsId, oddId, outcomeId }: GetOutcomeIdOrOddsIdFromPropsType = {},
) => oddsId ?? oddId ?? outcomeId;

export const getOutcomeIdsFromProps = (
  state: StateType,
  {
    outcomeIds,
  }: {
    outcomeIds?: Array<string | number>;
  } = {},
) => outcomeIds;

export type GetBetIdFromPropsType = {
  betId?: number;
};
export const getBetIdFromProps = (state: StateType, { betId }: GetBetIdFromPropsType = {}) => betId;

export type GetRouteFromPropsType = {
  route?: Route;
};
export const getRouteFromProps = (state: StateType, { route }: GetRouteFromPropsType = {}) => route;

export type GetIndexFromPropsType = {
  index?: number;
};

export const getIndexFromProps = (state: StateType, { index }: GetIndexFromPropsType = {}) => index;

export type GetSelectedSportIdFromPropsType = {
  selectedSportId: number;
};
export const getSelectedSportIdFromProps = (
  state: StateType,
  { selectedSportId }: GetSelectedSportIdFromPropsType = {} as GetSelectedSportIdFromPropsType,
) => selectedSportId;

export type GetIdFromPropsType = {
  id?: number;
};
export const getIdFromProps = (_: StateType, { id }: GetIdFromPropsType = {}) => id;

export type GetOutcomesFromPropsType = {
  outcomes: Array<OutcomeState>;
};
export const getOutcomesFromProps = (
  _: StateType,
  { outcomes }: GetOutcomesFromPropsType = {} as GetOutcomesFromPropsType,
) => outcomes;

export type GetOutcomeFromPropsType = {
  outcome?: unknown;
};
export const getOutcomeFromProps = (_: StateType, { outcome }: GetOutcomeFromPropsType = {}) => outcome;

export type GetOutcomeIdFromPropsType = {
  outcomeId?: number;
  id?: number;
};
export const getOutcomeIdFromProps = (state: StateType, { outcomeId, id }: GetOutcomeIdFromPropsType = {}) =>
  outcomeId ?? id;

export type GetOutcomeStatusFromPropsType = {
  outcomeStatus?: OutcomeStatus;
};
export const getOutcomeStatusFromProps = (_: StateType, { outcomeStatus }: GetOutcomeStatusFromPropsType = {}) =>
  outcomeStatus;

export type GetFlashMessageIdFromPropsType = {
  flashMessageId?: string;
  id: string;
};
export const getFlashMessageIdFromProps = (
  _: StateType,
  { flashMessageId, id }: GetFlashMessageIdFromPropsType = {} as GetFlashMessageIdFromPropsType,
) => flashMessageId ?? id;

export type GetCompleteBetAllowedFromPropsType = {
  completeBetAllowed?: boolean;
};
export const getCompleteBetAllowedFromProps = (
  _: StateType,
  { completeBetAllowed }: GetCompleteBetAllowedFromPropsType = {},
) => completeBetAllowed ?? false;

export type GetCashoutAllowedFromPropsType = {
  cashoutAllowed?: boolean;
};
export const getCashoutAllowedFromProps = (_: StateType, { cashoutAllowed }: GetCashoutAllowedFromPropsType = {}) =>
  cashoutAllowed ?? false;

export type GetCashoutValueFromPropsType = {
  cashoutValue?: number;
};
export const getCashoutValueFromProps = (_: StateType, { cashoutValue }: GetCashoutValueFromPropsType = {}) =>
  cashoutValue;

export type GetMatchIdFromPropsType = {
  matchId?: number;
};
export const getMatchIdFromProps = (_: StateType, { matchId }: GetMatchIdFromPropsType = {}) => matchId;

export type GetCategoryIdFromPropsType = {
  categoryId?: number;
};
export const getCategoryIdFromProps = (_: StateType, { categoryId }: GetCategoryIdFromPropsType = {}) => categoryId;

export type GetCardModeFromPropsType = {
  cardMode?: CardMode;
};
export const getCardModeFromProps = (_: StateType, { cardMode }: GetCardModeFromPropsType = {}) => cardMode;

export type GetLocationFromPropsType = {
  location?: Location;
};
export const getLocationFromProps = (_: StateType, { location }: GetLocationFromPropsType = {}) => location;

export type GetSportIdFromPropsType = {
  sportId?: SportId;
};
export const getSportIdFromProps = (_: StateType, { sportId }: GetSportIdFromPropsType = {}) => sportId;

export type GetTournamentIdFromPropsType = {
  tournamentId?: number;
};
export const getTournamentIdFromProps = (_: StateType, { tournamentId }: GetTournamentIdFromPropsType = {}) =>
  tournamentId;

export type GetCalendarIdFromPropsType = {
  calendarId?: number;
};
export const getCalendarIdFromProps = (_: StateType, { calendarId }: GetCalendarIdFromPropsType = {}) => calendarId;

export type GetBetFilterIdFromPropsType = {
  betFilterId?: number;
};
export const getBetFilterIdFromProps = (_: StateType, { betFilterId }: GetBetFilterIdFromPropsType = {}) => betFilterId;

export type GetGridReferenceFromPropsType = {
  gridReference?: string;
};
export const getGridReferenceFromProps = (_: StateType, { gridReference }: GetGridReferenceFromPropsType = {}) =>
  gridReference;

export type GetPlayerIdFromPropsType = {
  playerId?: number;
};
export const getPlayerIdFromProps = (_: StateType, { playerId }: GetPlayerIdFromPropsType = {}) => playerId;

export type GetPoolIdFromPropsType = {
  poolId?: number;
};
export const getPoolIdFromProps = (_: StateType, { poolId }: GetPoolIdFromPropsType = {}) => poolId;

export type GetIsGridFromPropsType = {
  isGrid?: boolean;
};
export const getIsGridFromProps = (_: StateType, { isGrid }: GetIsGridFromPropsType = {}) => isGrid ?? false;

export type GetAchievementIdFromPropsType = {
  achievementId?: number;
};
export const getAchievementIdFromProps = (_: StateType, { achievementId }: GetAchievementIdFromPropsType = {}) =>
  achievementId;

export type GetMissionIdFromPropsType = {
  missionId?: number;
};
export const getMissionIdFromProps = (_: StateType, { missionId }: GetMissionIdFromPropsType = {}) => missionId;

export type GetListBetIdsFromPropsType = {
  listBetIds?: Array<number>;
};
export const getListBetIdsFromProps = (_: StateType, { listBetIds }: GetListBetIdsFromPropsType = {}) => listBetIds;

export type GetIsBetFilteredFromPropsType = {
  isBetFiltered?: boolean;
};
export const getIsBetFilteredFromProps = (_: StateType, { isBetFiltered }: GetIsBetFilteredFromPropsType = {}) =>
  isBetFiltered ?? false;

export type GetPathnameFromPropsType = {
  pathname?: string;
};
export const getPathnameFromProps = (_: StateType, { pathname }: GetPathnameFromPropsType = {}) => pathname;

export type GetSeparatorTypeFromPropsType = {
  separatorType?: SeparatorType;
};
export const getSeparatorTypeFromProps = (_: StateType, { separatorType }: GetSeparatorTypeFromPropsType = {}) =>
  separatorType;

export type GetPromotionImageFromPropsType = {
  promotionImage?: string;
};
export const getPromotionImageFromProps = (_: StateType, { promotionImage }: GetPromotionImageFromPropsType = {}) =>
  promotionImage;

export type GetPromotionUrlFromPropsType = {
  promotionUrl?: string;
};
export const getPromotionUrlFromProps = (_: StateType, { promotionUrl }: GetPromotionUrlFromPropsType = {}) =>
  promotionUrl;

export type GetPromotionWamUrlFromPropsType = {
  promotionWamUrl?: string;
};
export const getPromotionWamUrlFromProps = (_: StateType, { promotionWamUrl }: GetPromotionWamUrlFromPropsType = {}) =>
  promotionWamUrl;

export type GetSeenMissionsFromPropsType = {
  seenMissions?: Array<number>;
};
export const getSeenMissionsFromProps = (_: StateType, { seenMissions }: GetSeenMissionsFromPropsType = {}) =>
  seenMissions;

export type GetBetslipReferenceFromPropsType = {
  betslipReference?: string;
};
export const getBetslipReferenceFromProps = (_: StateType, { betslipReference }: GetBetslipReferenceFromPropsType) =>
  betslipReference;

export type GetCompetitorIdFromPropsType = {
  competitorId?: number;
};
export const getCompetitorIdFromProps = (_: StateType, { competitorId }: GetCompetitorIdFromPropsType = {}) =>
  competitorId;

export type GetSliderValueFromPropsType = { sliderValue?: string };
export const getSliderValueFromProps = (_: StateType, { sliderValue }: GetSliderValueFromPropsType = {}) => sliderValue;

export type GetIsFromMultichoiceFromPropsType = { isFromMultichoice?: boolean };
export const getIsFromMultichoiceFromProps = (
  _: StateType,
  { isFromMultichoice }: GetIsFromMultichoiceFromPropsType = {},
) => isFromMultichoice;

export type GetIsGoalkeeperFromPropsType = {
  isGoalKeeper?: boolean;
};
export const getIsGoalkeeperFromProps = (_: StateType, { isGoalKeeper }: GetIsGoalkeeperFromPropsType = {}) =>
  isGoalKeeper ?? false;

export type GetOddFromPropsType = {
  oddId?: number;
  oddsId?: Array<number>;
};

export const getOddIdFromProps = (_: StateType, { oddId, oddsId }: GetOddFromPropsType) => oddId ?? oddsId;

export type GetOddsFromPropsType = {
  odds?: number;
};
export const getOddsFromProps = (_: StateType, { odds }: GetOddsFromPropsType = {}) => odds;

export type GetHourFromPropsType = {
  hour?: number;
};
export const getHourFromProps = (_: StateType, { hour }: GetHourFromPropsType = {}) => hour;

export type GetTeamIdFromPropsType = {
  teamId?: number;
};
export const getTeamIdFromProps = (_: StateType, { teamId }: GetTeamIdFromPropsType = {}) => teamId;

export type GetLocationHashFromPropsType = {
  locationHash?: string;
};
export const getLocationHashFromProps = (_: StateType, { locationHash }: GetLocationHashFromPropsType = {}) =>
  locationHash;

export type GetFilterIdFromPropsType = {
  filterId?: string;
};
export const getFilterIdFromProps = (_: StateType, { filterId }: GetFilterIdFromPropsType = {}) => filterId;

export type GetBetsDataFromPropsType = {
  betsData?: Array<{
    betId: number;
    isMultichoice: boolean;
  }>;
};
export const getBetsDataFromProps = (_: StateType, { betsData }: GetBetsDataFromPropsType = {}) => betsData;

export type GetTabBarItemIdFromPropsType = {
  tabBarItemId?: TabBarNavigationType;
};
export const getTabBarItemIdFromProps = (_: StateType, { tabBarItemId }: GetTabBarItemIdFromPropsType = {}) =>
  tabBarItemId;

export type GetHistoryFromPropsType = {
  history?: CustomRouterHistory;
};
export const getHistoryFromProps = (_: StateType, { history }: GetHistoryFromPropsType = {}) => history;

export type GetDispatchFromPropsType = {
  dispatch?: Dispatch<AnyAction>;
};
export const getDispatchFromProps = (_: StateType, { dispatch }: GetDispatchFromPropsType = {}) => dispatch;

export type GetIsHighlightFromPropsType = {
  isHighlight?: boolean;
};
export const getIsHighlightFromProps = (_: StateType, { isHighlight }: GetIsHighlightFromPropsType = {}) =>
  isHighlight ?? false;

export type GetBetTemplateFromPropsType = {
  betTemplate?: BetTemplate;
};
export const getBetTemplateFromProps = (_: StateType, { betTemplate }: GetBetTemplateFromPropsType = {}) => betTemplate;

export type GetShowPercentDistribtionFromPropsType = {
  showPercentDistribution?: boolean;
};
export const getShowPercentDistribtionFromProps = (
  _: StateType,
  { showPercentDistribution }: GetShowPercentDistribtionFromPropsType = {},
) => showPercentDistribution ?? false;

export type GetMatchIsAvailableFromPropsType = {
  matchIsAvailable?: boolean;
};
export const getMatchIsAvailableFromProps = (
  _: StateType,
  { matchIsAvailable }: GetMatchIsAvailableFromPropsType = {},
) => matchIsAvailable ?? false;

export type GetDisplayLabelFromPropsType = {
  displayLabel?: boolean;
};
export const getDisplayLabelFromProps = (_: StateType, { displayLabel }: GetDisplayLabelFromPropsType = {}) =>
  displayLabel ?? false;

export type GetMatchIdFromContextFromPropsType = {
  matchIdFromContext?: number;
};
export const getMatchIdFromContextFromProps = (
  _: StateType,
  { matchIdFromContext }: GetMatchIdFromContextFromPropsType = {},
) => matchIdFromContext;

export type GetLocationPathnameFromPropsType = {
  location?: Location;
};
export const getLocationPathnameFromProps = (_: StateType, { location }: GetLocationPathnameFromPropsType = {}) =>
  location?.pathname;

export type GetHashLocationFromPropsType = {
  location?: Location;
};
export const getHashLocationFromProps = (_: StateType, { location }: GetHashLocationFromPropsType = {}) =>
  location?.hash;

export type GetOpenExternalLinkFromPropsType = {
  openExternalLink?: (url: string, options?: unknown) => void;
};
export const getOpenExternalLinkFromProps = (
  _: StateType,
  { openExternalLink }: GetOpenExternalLinkFromPropsType = {},
) => openExternalLink;

export type GetToggleMenuFromPropsType = {
  toggleMenu?: boolean;
};
export const getToggleMenuFromProps = (_: StateType, { toggleMenu }: GetToggleMenuFromPropsType) => toggleMenu;

export type GetClearPreferencesFromPropsType = {
  clearPreferences?: (id: string) => void;
};
export const getClearPreferencesFromProps = (_: StateType, { clearPreferences }: GetClearPreferencesFromPropsType) =>
  clearPreferences;

export type GetOpenGRViewFromPropsType = {
  openGRView?: () => void;
};
export const getOpenGRViewFromProps = (_: StateType, { openGRView }: GetOpenGRViewFromPropsType) => openGRView;

export type GetOpenBoomViewFromPropsType = {
  openBoomView?: () => void;
};
export const getOpenBoomViewFromProps = (_: StateType, { openBoomView }: GetOpenBoomViewFromPropsType) => openBoomView;

export type GetIsHotFilterSelectedPropsType = {
  isHotFilterSelected?: boolean;
};
export const getIsHotFilterSelectedFromProps = (
  _: StateType,
  { isHotFilterSelected }: GetIsHotFilterSelectedPropsType = {},
) => isHotFilterSelected ?? false;

export type GetNoLabelFromPropsType = {
  noLabel?: boolean;
};
export const getNoLabelFromProps = (_: StateType, { noLabel }: GetNoLabelFromPropsType = {}) => noLabel ?? false;

export type GetIsMultichoiceFromPropsType = {
  isMultichoice?: boolean;
};
export const getIsMultichoiceFromProps = (_: StateType, { isMultichoice }: GetIsMultichoiceFromPropsType = {}) =>
  isMultichoice ?? false;

export type GetRoundIdFromPropsType = {
  roundId: GRRound;
};

export const getRoundIdFromProps = (_: StateType, { roundId }: GetRoundIdFromPropsType) => roundId;

export type GetBetCategoryIdFromPropsType = {
  betCategoryId?: number;
};
export const getBetCategoryIdFromProps = (_: StateType, { betCategoryId }: GetBetCategoryIdFromPropsType = {}) =>
  betCategoryId;

export type GetIsOutrightFromPropsType = {
  isOutright?: boolean;
};
export const getIsOutrightFromProps = (_: StateType, { isOutright }: GetIsOutrightFromPropsType = {}) =>
  isOutright ?? false;

export type GetIsFromPlayerRowFromPropsType = {
  isFromPlayerRow?: boolean;
};
export const getIsFromPlayerRowFromProps = (_: StateType, { isFromPlayerRow }: GetIsFromPlayerRowFromPropsType = {}) =>
  isFromPlayerRow ?? false;

export type GetIsLoadingFromPropsType = {
  isLoading?: boolean;
};
export const getIsLoadingFromProps = (_: StateType, { isLoading }: GetIsLoadingFromPropsType = {}) =>
  isLoading ?? false;
